import React, { useCallback, useEffect, useRef, useState } from 'react';
import config from '../config';
import MT5HistoryIcon from './MT5/MT5HistoryIcon';

const readResponseError = async (res) => {
  const status = res?.status;
  const contentType = res?.headers?.get?.('content-type') || '';
  let text = '';
  try {
    text = await res.text();
  } catch (e) {
    text = '';
  }

  let msg = '';
  if (contentType.includes('application/json')) {
    try {
      const j = JSON.parse(text || '{}');
      msg = j?.detail || j?.message || '';
    } catch (e) {
      msg = '';
    }
  }
  if (!msg) msg = (text || '').trim();
  if (!msg) msg = `HTTP ${status}`;
  if (msg.length > 300) msg = msg.slice(0, 300) + '...';
  return msg;
};

const Affiliate = () => {
  const [visible, setVisible] = useState(false);
  const [hideAccountDetails, setHideAccountDetails] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [positionsByAccount, setPositionsByAccount] = useState({});
  const [positionsLoadingByAccount, setPositionsLoadingByAccount] = useState({});
  const positionsRef = useRef(positionsByAccount);
  const accountsRef = useRef(accounts);
  const refreshLoopInFlightRef = useRef(false);
  // Per-account cashflow summary (loaded only when History is opened)
  const [cashflowByAccount, setCashflowByAccount] = useState({});
  // New: per-account buy/sell lots summary
  const [buySellByAccount, setBuySellByAccount] = useState({});

  const computeBuySellFromList = useCallback((list) => {
    const arr = Array.isArray(list) ? list : [];
    let buy = 0, sell = 0;
    for (const p of arr) {
      const isPending = !!(p?.is_pending || String(p?.type || '').includes('LIMIT') || String(p?.type || '').includes('STOP'));
      if (isPending) continue;
      const t = String(p?.type || '').toUpperCase();
      const vol = Number(p?.volume || 0) || 0;
      if (t === 'BUY') buy += vol;
      else if (t === 'SELL') sell += vol;
    }
    const net = buy - sell;
    const direction = net > 0 ? 'Long' : net < 0 ? 'Short' : 'Flat';
    return { buy, sell, net, direction };
  }, []);
  
  // Trading form state
  const [tradeForm, setTradeForm] = useState({
    symbol: 'EURUSD',
    orderType: 'market',
    action: 'buy',
    equityPercent: 10,
    tpPercent: '',
    slPercent: '',
    pendingPrice: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [closingMarket, setClosingMarket] = useState(false);
  const [cancelingPending, setCancelingPending] = useState(false);
  const [loadingAllPositions, setLoadingAllPositions] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState({});
  const [accountSortOrder, setAccountSortOrder] = useState('asc'); // 'asc' or 'desc'

  const getAuthToken = useCallback(() => localStorage.getItem('token'), []);

  const handleUnauthorized = useCallback((message = 'Session expired. Please log in again.') => {
    localStorage.removeItem('token');
    localStorage.removeItem('isAdmin');
    setError(message);
    window.location.href = '/login';
  }, []);

  const withAuthHeaders = useCallback((headers = {}) => {
    const token = getAuthToken();
    if (!token) return null;
    return { ...headers, Authorization: `Bearer ${token}` };
  }, [getAuthToken]);

  useEffect(() => {
    positionsRef.current = positionsByAccount;
  }, [positionsByAccount]);

  useEffect(() => {
    accountsRef.current = accounts;
  }, [accounts]);

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        // Check if tab is visible and mappings exist
        const headers = withAuthHeaders();
        if (!headers) {
          handleUnauthorized();
          return;
        }
        const res = await fetch(`${config.apiUrl}/affiliate/summary`, { headers });
        if (res.status === 401) {
          handleUnauthorized();
          return;
        }
        if (!res.ok) {
          throw new Error(`Failed to load affiliate summary: ${await readResponseError(res)}`);
        }
        const summary = await res.json();
        setVisible(!!summary.affiliate_tab_visible);
        setHideAccountDetails(!!summary.affiliate_hide_account_details);
        if (!summary.affiliate_tab_visible) {
          setAccounts([]);
          return;
        }
        // Load accounts list
        const res2 = await fetch(`${config.apiUrl}/affiliate/accounts`, { headers });
        if (res2.status === 401) {
          handleUnauthorized();
          return;
        }
        if (!res2.ok) {
          throw new Error(`Failed to load affiliate accounts: ${await readResponseError(res2)}`);
        }
        const data = await res2.json();
        const list = Array.isArray(data) ? data : [];
        setAccounts(list);
        // Initialize all accounts as selected by default
        const initialSelection = {};
        list.forEach(item => {
          if (item.account?.account_number) {
            initialSelection[item.account.account_number] = true;
          }
        });
        setSelectedAccounts(initialSelection);
        // Cashflow is loaded only when user opens History for an account.
        // Trigger a one-time buy/sell summary fetch per account (do not block UI)
        // NOTE: Buy/Sell lots are now computed from loaded positions to avoid extra API calls.
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [withAuthHeaders, handleUnauthorized]);

  const fetchAccounts = useCallback(async () => {
    try {
      const headers = withAuthHeaders();
      if (!headers) {
        handleUnauthorized();
        return [];
      }
      const res2 = await fetch(`${config.apiUrl}/affiliate/accounts`, { headers });
      if (res2.status === 401) {
        handleUnauthorized();
        return [];
      }
      if (!res2.ok) {
        throw new Error(`Failed to load affiliate accounts: ${await readResponseError(res2)}`);
      }
      const data = await res2.json();
      const list = Array.isArray(data) ? data : [];
      setAccounts(list);
      return list;
      // Fetch buy/sell summary once for new accounts
      // NOTE: Buy/Sell lots are now computed from loaded positions to avoid extra API calls.
    } catch (e) {
      // show once in the header area
      setError(e.message);
      return [];
    }
  }, [withAuthHeaders, handleUnauthorized]);

  const fetchPositions = useCallback(async (accountNumber, { silent = false } = {}) => {
    try {
      const headers = withAuthHeaders();
      if (!headers) {
        handleUnauthorized();
        return;
      }
      const res = await fetch(`${config.apiUrl}/affiliate/accounts/${accountNumber}/positions`, { headers });
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      if (!res.ok) throw new Error('Failed to load positions');
      const data = await res.json();

      // Always update lots summary, even if positions panel is closed.
      setBuySellByAccount(prev => ({ ...prev, [accountNumber]: computeBuySellFromList(data) }));

      // Only update the positions table if the panel is currently open.
      if (Object.prototype.hasOwnProperty.call(positionsRef.current || {}, accountNumber)) {
        setPositionsByAccount(prev => {
          const next = { ...prev, [accountNumber]: data };
          positionsRef.current = next;
          return next;
        });
        setPositionsLoadingByAccount(prev => {
          if (!Object.prototype.hasOwnProperty.call(prev || {}, accountNumber)) return prev;
          return { ...prev, [accountNumber]: false };
        });
      }
    } catch (e) {
      if (!silent) alert(e.message);
      setPositionsLoadingByAccount(prev => {
        if (!Object.prototype.hasOwnProperty.call(prev || {}, accountNumber)) return prev;
        return { ...prev, [accountNumber]: false };
      });
    }
  }, [withAuthHeaders, handleUnauthorized, computeBuySellFromList]);

  // Fetch positions for all selected accounts in parallel
  const fetchAllSelectedPositions = useCallback(async () => {
    const selectedAccountNumbers = accounts
      .filter(item => selectedAccounts[item.account.account_number])
      .map(item => item.account.account_number);
    
    if (selectedAccountNumbers.length === 0) return;
    
    await Promise.all(
      selectedAccountNumbers.map(accNum => fetchPositions(accNum, { silent: true }))
    );
  }, [accounts, selectedAccounts, fetchPositions]);

  // Load all positions in parallel (toggles open/close)
  const handleLoadAllPositions = useCallback(async () => {
    const allOpen = accounts.length > 0 && accounts.every(item =>
      Object.prototype.hasOwnProperty.call(positionsRef.current || {}, item.account.account_number)
    );

    if (allOpen) {
      setPositionsByAccount(() => {
        const next = {};
        positionsRef.current = next;
        return next;
      });
      setPositionsLoadingByAccount({});
      return;
    }

    setLoadingAllPositions(true);
    const startTime = Date.now();

    // Mark all accounts as open immediately so a second click can close,
    // and so in-flight refreshes are guarded properly.
    setPositionsByAccount(prev => {
      const next = { ...prev };
      for (const item of accounts) {
        const acc = item.account.account_number;
        if (!Object.prototype.hasOwnProperty.call(next, acc)) next[acc] = [];
      }
      positionsRef.current = next;
      return next;
    });
    setPositionsLoadingByAccount(prev => {
      const next = { ...(prev || {}) };
      for (const item of accounts) {
        const acc = item.account.account_number;
        if (!Object.prototype.hasOwnProperty.call(next, acc)) next[acc] = true;
      }
      return next;
    });

    try {
      await Promise.all(
        accounts.map(item => fetchPositions(item.account.account_number, { silent: true }))
      );
      const duration = ((Date.now() - startTime) / 1000).toFixed(2);
      console.log(`Loaded positions for ${accounts.length} accounts in ${duration}s`);
    } catch (e) {
      console.error('Error loading all positions:', e);
    } finally {
      setLoadingAllPositions(false);
    }
  }, [accounts, fetchPositions]);

  const togglePositions = (accountNumber) => {
    setPositionsByAccount(prev => {
      if (Object.prototype.hasOwnProperty.call(prev || {}, accountNumber)) {
        const next = { ...prev };
        delete next[accountNumber];
        positionsRef.current = next;
        setPositionsLoadingByAccount(plPrev => {
          if (!Object.prototype.hasOwnProperty.call(plPrev || {}, accountNumber)) return plPrev;
          const plNext = { ...(plPrev || {}) };
          delete plNext[accountNumber];
          return plNext;
        });
        return next;
      }

      // Open immediately, then load.
      const next = { ...prev, [accountNumber]: [] };
      positionsRef.current = next;
      setPositionsLoadingByAccount(plPrev => ({ ...(plPrev || {}), [accountNumber]: true }));
      fetchPositions(accountNumber, { silent: false });
      return next;
    });
  };

  const affiliateRefreshMs = Math.max(1000, Number(config.affiliateRefreshMs) || 5000);

  // Sequential refresh cycle:
  // 1) Refresh account summaries (all accounts)
  // 2) Fetch positions sequentially for each account (updates lots; updates table if open)
  // 3) Repeat only after all accounts processed
  useEffect(() => {
    if (!visible) return;

    let stopped = false;

    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    const runLoop = async () => {
      if (refreshLoopInFlightRef.current) return;
      refreshLoopInFlightRef.current = true;
      try {
        while (!stopped) {
          const list = await fetchAccounts();
          const snapshot = Array.isArray(list) && list.length ? list : (accountsRef.current || []);

          for (const item of snapshot) {
            if (stopped) break;
            const accNum = item?.account?.account_number;
            if (!accNum) continue;
            await fetchPositions(accNum, { silent: true });
          }

          if (stopped) break;
          await sleep(affiliateRefreshMs);
        }
      } finally {
        refreshLoopInFlightRef.current = false;
      }
    };

    runLoop();
    return () => {
      stopped = true;
    };
  }, [visible, fetchAccounts, fetchPositions, affiliateRefreshMs]);

  const fmt = (n) => {
    const v = Number(n ?? 0);
    if (Number.isNaN(v)) return '-';
    return v.toFixed(2);
  };

  // Format price with 5 decimals for open price
  const fmtPrice = (n) => {
    const v = Number(n ?? 0);
    if (Number.isNaN(v)) return '-';
    return v.toFixed(5);
  };

  // Format open time minus 2 hours
  const fmtOpenTime = (p) => {
    let ts = null;
    if (p.open_time) {
      ts = new Date(p.open_time);
    } else if (p.time) {
      ts = new Date(p.time * 1000);
    }
    if (!ts || isNaN(ts.getTime())) return '';
    const adjusted = new Date(ts.getTime() - 2 * 3600 * 1000);
    return adjusted.toLocaleString();
  };

  const toggleAccountSelection = (accountNumber) => {
    setSelectedAccounts(prev => ({
      ...prev,
      [accountNumber]: !prev[accountNumber]
    }));
  };
  
  const toggleSelectAll = () => {
    const allSelected = accounts.every(item => selectedAccounts[item.account.account_number]);
    const newSelection = {};
    accounts.forEach(item => {
      if (item.account?.account_number) {
        newSelection[item.account.account_number] = !allSelected;
      }
    });
    setSelectedAccounts(newSelection);
  };

  const handleOpenPosition = async (actionOverride = null) => {
    const action = actionOverride !== null ? actionOverride : tradeForm.action;
    if (!tradeForm.symbol.trim()) {
      alert('Please enter a symbol');
      return;
    }
    
    // Check if any accounts are selected
    const selectedCount = Object.values(selectedAccounts).filter(Boolean).length;
    if (selectedCount === 0) {
      alert('Please select at least one account');
      return;
    }
    
    if (tradeForm.orderType !== 'market' && !tradeForm.pendingPrice) {
      alert('Please enter a pending price for stop/limit orders');
      return;
    }
    
    setSubmitting(true);
    const startTime = Date.now();
    
    try {
      // Get list of selected account numbers
      const selectedAccountNumbers = accounts
        .filter(item => selectedAccounts[item.account.account_number])
        .map(item => item.account.account_number);
      
      const payload = {
        symbol: tradeForm.symbol.toUpperCase(),
        order_type: tradeForm.orderType,
        action: action,
        equity_percent: parseFloat(tradeForm.equityPercent) || 10,
        tp_percent: tradeForm.tpPercent ? parseFloat(tradeForm.tpPercent) : null,
        sl_percent: tradeForm.slPercent ? parseFloat(tradeForm.slPercent) : null,
        pending_price: tradeForm.pendingPrice ? parseFloat(tradeForm.pendingPrice) : null,
        account_numbers: selectedAccountNumbers
      };
      
      const res = await fetch(`${config.apiUrl}/affiliate/open-position-all`, {
        method: 'POST',
        headers: withAuthHeaders({
          'Content-Type': 'application/json'
        }) || {},
        body: JSON.stringify(payload)
      });

      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to open positions');
      }
      
      const duration = ((Date.now() - startTime) / 1000).toFixed(2);
      
      // Show results
      const successCount = data.results.filter(r => r.status === 'success').length;
      const errorCount = data.results.filter(r => r.status === 'error').length;
      const notActivatedAccounts = data.results.filter(r => 
        r.status === 'error' && r.error && r.error.includes('not activated')
      );
      
      let message = `✅ Opened positions on ${successCount} account(s) in ${duration}s (parallel execution)`;
      
      if (successCount > 0) {
        message += '\n\nSuccessful trades:';
        data.results.filter(r => r.status === 'success').forEach(r => {
          message += `\n✓ Account ${r.account_number}: Ticket ${r.ticket}, Volume ${r.volume}, Price ${r.price}`;
        });
      }
      
      if (notActivatedAccounts.length > 0) {
        message += `\n\n⚠️ ${notActivatedAccounts.length} account(s) need activation first:`;
        notActivatedAccounts.forEach(r => {
          message += `\n• Account ${r.account_number} - Please activate this account on the MT5 service machine first`;
        });
      }
      
      if (errorCount > notActivatedAccounts.length) {
        message += `\n\n❌ ${errorCount - notActivatedAccounts.length} other error(s):`;
        data.results.filter(r => r.status === 'error' && !(r.error && r.error.includes('not activated'))).forEach(r => {
          message += `\n✗ Account ${r.account_number}: ${r.error}`;
        });
      }
      
      alert(message);
      
      // Refresh positions for all open accounts in parallel
      const refreshPromises = Object.keys(positionsByAccount).map(acc => 
        fetchPositions(acc, { silent: true })
      );
      await Promise.all(refreshPromises);
      
    } catch (e) {
      alert(`Error: ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleClosePosition = async (accountNumber, ticket) => {
    if (!window.confirm(`Close position ${ticket}?`)) return;
    
    try {
      const res = await fetch(`${config.apiUrl}/affiliate/close-position`, {
        method: 'POST',
        headers: withAuthHeaders({
          'Content-Type': 'application/json'
        }) || {},
        body: JSON.stringify({
          account_number: accountNumber,
          ticket: ticket
        })
      });

      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to close position');
      }
      
      alert(`Position ${ticket} closed successfully`);
      
      // Refresh positions for this account
      fetchPositions(accountNumber, { silent: true });
      
    } catch (e) {
      alert(`Error closing position: ${e.message}`);
    }
  };

  const handleCloseAllMarketPositions = async () => {
    const selectedAccountNumbers = accounts
      .filter(item => selectedAccounts[item.account.account_number])
      .map(item => item.account.account_number);
    
    if (selectedAccountNumbers.length === 0) {
      alert('Please select at least one account');
      return;
    }

    // First fetch positions for selected accounts in parallel
    await fetchAllSelectedPositions();

    // Collect all positions with Affiliate comment
    const positionsToClose = [];
    selectedAccountNumbers.forEach(accNum => {
      const positions = positionsByAccount[accNum] || [];
      positions.forEach(pos => {
        // Only close market positions (not pending orders) with Affiliate comment
        const isPending = pos.is_pending || pos.type?.includes('LIMIT') || pos.type?.includes('STOP');
        const hasAffiliateComment = pos.comment && (pos.comment.includes('Affiliate') || pos.comment.includes('Close by Affiliate'));
        if (!isPending && hasAffiliateComment) {
          positionsToClose.push({ accountNumber: accNum, ticket: pos.ticket, symbol: pos.symbol });
        }
      });
    });

    if (positionsToClose.length === 0) {
      alert('No market positions with Affiliate comment found on selected accounts');
      return;
    }

    if (!window.confirm(`Close ${positionsToClose.length} market position(s) with Affiliate comment?`)) return;

    setClosingMarket(true);
    const startTime = Date.now();

    try {
      // Close all positions in parallel
      const results = await Promise.allSettled(
        positionsToClose.map(async ({ accountNumber, ticket }) => {
          const res = await fetch(`${config.apiUrl}/affiliate/close-position`, {
            method: 'POST',
            headers: withAuthHeaders({
              'Content-Type': 'application/json'
            }) || {},
            body: JSON.stringify({ account_number: accountNumber, ticket })
          });
          if (res.status === 401) {
            handleUnauthorized();
            throw new Error('Unauthorized');
          }
          const data = await res.json();
          if (!res.ok) throw new Error(data.detail || 'Failed');
          return { accountNumber, ticket, status: 'success' };
        })
      );

      const duration = ((Date.now() - startTime) / 1000).toFixed(2);
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      let message = `✅ Closed ${successful} market position(s) in ${duration}s`;
      if (failed > 0) {
        message += `\n❌ Failed to close ${failed} position(s)`;
      }
      alert(message);

      // Refresh positions for all selected accounts
      await fetchAllSelectedPositions();

    } catch (e) {
      alert(`Error: ${e.message}`);
    } finally {
      setClosingMarket(false);
    }
  };

  const handleCancelAllPendingOrders = async () => {
    const selectedAccountNumbers = accounts
      .filter(item => selectedAccounts[item.account.account_number])
      .map(item => item.account.account_number);
    
    if (selectedAccountNumbers.length === 0) {
      alert('Please select at least one account');
      return;
    }

    // First fetch positions for selected accounts in parallel
    await fetchAllSelectedPositions();

    // Collect all pending orders with Affiliate comment
    const ordersToCancel = [];
    selectedAccountNumbers.forEach(accNum => {
      const positions = positionsByAccount[accNum] || [];
      positions.forEach(pos => {
        // Only cancel pending orders with Affiliate comment
        const isPending = pos.is_pending || pos.type?.includes('LIMIT') || pos.type?.includes('STOP');
        const hasAffiliateComment = pos.comment && (pos.comment.includes('Affiliate') || pos.comment.includes('Close by Affiliate'));
        if (isPending && hasAffiliateComment) {
          ordersToCancel.push({ accountNumber: accNum, ticket: pos.ticket, symbol: pos.symbol, type: pos.type });
        }
      });
    });

    if (ordersToCancel.length === 0) {
      alert('No pending orders with Affiliate comment found on selected accounts');
      return;
    }

    if (!window.confirm(`Cancel ${ordersToCancel.length} pending order(s) with Affiliate comment?`)) return;

    setCancelingPending(true);
    const startTime = Date.now();

    try {
      // Cancel all orders in parallel
      const results = await Promise.allSettled(
        ordersToCancel.map(async ({ accountNumber, ticket }) => {
          const res = await fetch(`${config.apiUrl}/affiliate/close-position`, {
            method: 'POST',
            headers: withAuthHeaders({
              'Content-Type': 'application/json'
            }) || {},
            body: JSON.stringify({ account_number: accountNumber, ticket })
          });
          if (res.status === 401) {
            handleUnauthorized();
            throw new Error('Unauthorized');
          }
          const data = await res.json();
          if (!res.ok) throw new Error(data.detail || 'Failed');
          return { accountNumber, ticket, status: 'success' };
        })
      );

      const duration = ((Date.now() - startTime) / 1000).toFixed(2);
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      let message = `✅ Canceled ${successful} pending order(s) in ${duration}s`;
      if (failed > 0) {
        message += `\n❌ Failed to cancel ${failed} order(s)`;
      }
      alert(message);

      // Refresh positions for all selected accounts
      await fetchAllSelectedPositions();

    } catch (e) {
      alert(`Error: ${e.message}`);
    } finally {
      setCancelingPending(false);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="text-red-600">Error: {error}</div>;
  if (!visible) return <div>The Affiliate tab is not enabled for your account.</div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Affiliate Accounts</h1>
      
      {/* Trading Controls */}
      <div className="bg-white rounded shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Open Position on All Accounts</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Symbol</label>
            <input
              type="text"
              className="w-full px-3 py-2 border rounded"
              value={tradeForm.symbol}
              onChange={(e) => setTradeForm({...tradeForm, symbol: e.target.value})}
              placeholder="EURUSD"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Order Type</label>
            <select
              className={`w-full px-3 py-2 border rounded font-semibold ${
                tradeForm.action === 'buy' 
                  ? 'bg-green-50 border-green-300 text-green-700' 
                  : 'bg-red-50 border-red-300 text-red-700'
              }`}
              value={tradeForm.orderType}
              onChange={(e) => setTradeForm({...tradeForm, orderType: e.target.value})}
            >
              <option value="market">Market</option>
              <option value="buy_stop">Buy Stop</option>
              <option value="sell_stop">Sell Stop</option>
              <option value="buy_limit">Buy Limit</option>
              <option value="sell_limit">Sell Limit</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Action</label>
            <select
              className={`w-full px-3 py-2 border rounded font-semibold ${
                tradeForm.action === 'buy' 
                  ? 'bg-green-50 border-green-300 text-green-700' 
                  : 'bg-red-50 border-red-300 text-red-700'
              }`}
              value={tradeForm.action}
              onChange={(e) => setTradeForm({...tradeForm, action: e.target.value})}
            >
              <option value="buy">Buy</option>
              <option value="sell">Sell</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Equity % to Use</label>
            <input
              type="number"
              className="w-full px-3 py-2 border rounded"
              value={tradeForm.equityPercent}
              onChange={(e) => setTradeForm({...tradeForm, equityPercent: e.target.value})}
              placeholder="10"
              step="0.1"
              min="0.1"
              max="100"
            />
          </div>
          
          {tradeForm.orderType !== 'market' && (
            <div>
              <label className="block text-sm font-medium mb-1">Pending Price</label>
              <input
                type="number"
                className="w-full px-3 py-2 border rounded"
                value={tradeForm.pendingPrice}
                onChange={(e) => setTradeForm({...tradeForm, pendingPrice: e.target.value})}
                placeholder="1.05000"
                step="0.00001"
              />
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium mb-1">TP % (optional)</label>
            <input
              type="number"
              className="w-full px-3 py-2 border rounded"
              value={tradeForm.tpPercent}
              onChange={(e) => setTradeForm({...tradeForm, tpPercent: e.target.value})}
              placeholder="2.0"
              step="0.1"
              min="0"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">SL % (optional)</label>
            <input
              type="number"
              className="w-full px-3 py-2 border rounded"
              value={tradeForm.slPercent}
              onChange={(e) => setTradeForm({...tradeForm, slPercent: e.target.value})}
              placeholder="1.0"
              step="0.1"
              min="0"
            />
          </div>
          
          <div className="flex items-end gap-2">
            <button
              className="flex-1 px-4 py-2 text-white rounded font-semibold bg-green-600 hover:bg-green-700 disabled:bg-gray-400"
              onClick={() => handleOpenPosition('buy')}
              disabled={submitting}
            >
              {submitting ? 'Opening...' : 'Buy All Selected'}
            </button>
            <button
              className="flex-1 px-4 py-2 text-white rounded font-semibold bg-red-600 hover:bg-red-700 disabled:bg-gray-400"
              onClick={() => handleOpenPosition('sell')}
              disabled={submitting}
            >
              {submitting ? 'Opening...' : 'Sell All Selected'}
            </button>
          </div>
        </div>
        <div className="flex gap-3 mt-4">
          <button
            className="px-6 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400"
            onClick={handleCloseAllMarketPositions}
            disabled={closingMarket || submitting || cancelingPending}
          >
            {closingMarket ? 'Closing...' : 'Close Market Positions (Affiliate)'}
          </button>
          <button
            className="px-6 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:bg-gray-400"
            onClick={handleCancelAllPendingOrders}
            disabled={cancelingPending || submitting || closingMarket}
          >
            {cancelingPending ? 'Canceling...' : 'Cancel Pending Orders (Affiliate)'}
          </button>
        </div>
      </div>
      
      {accounts.length === 0 ? (
        <div>No affiliated accounts assigned yet.</div>
      ) : (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={accounts.every(item => selectedAccounts[item.account.account_number])}
                onChange={toggleSelectAll}
                className="w-4 h-4 cursor-pointer"
              />
              <span className="text-sm font-medium">
                {Object.values(selectedAccounts).filter(Boolean).length} of {accounts.length} accounts selected for trading
              </span>
              <div className="ml-4 flex items-center gap-1">
                <span className="text-sm text-gray-600">Sort:</span>
                <button
                  className={`px-2 py-1 text-xs rounded ${accountSortOrder === 'asc' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                  onClick={() => setAccountSortOrder('asc')}
                  title="Sort accounts ascending"
                >
                  ▲ Asc
                </button>
                <button
                  className={`px-2 py-1 text-xs rounded ${accountSortOrder === 'desc' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                  onClick={() => setAccountSortOrder('desc')}
                  title="Sort accounts descending"
                >
                  ▼ Desc
                </button>
              </div>
            </div>
            <button
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-gray-400 text-sm font-medium"
              onClick={handleLoadAllPositions}
              disabled={loadingAllPositions}
            >
              {loadingAllPositions ? 'Loading...' : `Load All Positions (${accounts.length})`}
            </button>
          </div>
          {[...accounts].sort((a, b) => {
            const aNum = parseInt(a.account.account_number) || 0;
            const bNum = parseInt(b.account.account_number) || 0;
            return accountSortOrder === 'asc' ? aNum - bNum : bNum - aNum;
          }).map(item => {
            const accNum = item.account.account_number;
            const cash = cashflowByAccount[accNum];
            const isSelected = selectedAccounts[accNum];
            return (
            <div key={item.mapping_id} className={`bg-white rounded shadow p-4 ${isSelected ? 'ring-2 ring-green-500' : ''} ${(() => {
              const s = buySellByAccount[accNum];
              if (!s) return '';
              if (s.net > 0) return 'border-l-4 border-green-500';
              if (s.net < 0) return 'border-l-4 border-red-500';
              return 'border-l-4 border-gray-300';
            })()}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={isSelected || false}
                    onChange={() => toggleAccountSelection(accNum)}
                    className="w-5 h-5 cursor-pointer"
                    title={isSelected ? 'Exclude from trading' : 'Include in trading'}
                  />
                  <div>
                    {!hideAccountDetails && item.account.account_number}
                    {!hideAccountDetails && item.owner?.email && (
                      <span className="ml-2 text-sm text-gray-600">({item.owner.email})</span>
                    )}
                    {hideAccountDetails && (
                      <span className="text-gray-400 italic text-sm">Account #{accounts.indexOf(item) + 1}</span>
                    )}
                  </div>
                  <div className="text-sm mt-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
                    <div>Balance: <span className="font-mono">{fmt(item.balance)}</span></div>
                    <div>Equity: <span className="font-mono">{fmt(item.equity)}</span></div>
                    <div>
                      {(() => {
                        const bal = Number(item.balance || 0);
                        const eq = Number(item.equity || 0);
                        const fpl = Number(item.floating_pl ?? (eq - bal));
                        const ddPct = bal > 0 ? (((eq - bal) / bal) * 100) : 0;
                        const ddPctStr = `${ddPct.toFixed(2)}%`;
                        return (
                          <span>
                            Floating P/L <span className="text-xs text-gray-600">(DD: {ddPctStr})</span>: {' '}
                            <span className={`font-mono ${fpl >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmt(fpl)}</span>
                          </span>
                        );
                      })()}
                    </div>
                    <div>
                      {(() => {
                        const s = buySellByAccount[accNum];
                        if (!s) return <span>Lots: ...</span>;
                        const netAbs = Math.abs(Number(s.net || 0));
                        return (
                          <span>
                            Lots: Buy <span className="font-mono">{fmt(s.buy)}</span> / Sell <span className="font-mono">{fmt(s.sell)}</span> — Net <span className="font-mono">{fmt(netAbs)}</span> {s.direction}
                          </span>
                        );
                      })()}
                    </div>
                    <div title={(() => { const arr = (cash?.depositEntries || []).slice(-5); return arr.map(e => `${new Date(e.time*1000).toLocaleDateString()}  +${fmt(e.amount)}`).join('\n'); })()}>
                      Deposits (12m): <span className="font-mono text-green-700">{cash ? fmt(cash.deposits) : 'Click history'}</span>
                      {cash?.lastDeposit && (
                        <span className="ml-2 text-xs text-gray-600">(last: {new Date(cash.lastDeposit.time*1000).toLocaleDateString()})</span>
                      )}
                    </div>
                    <div title={(() => { const arr = (cash?.withdrawalEntries || []).slice(-5); return arr.map(e => `${new Date(e.time*1000).toLocaleDateString()}  ${fmt(e.amount)}`).join('\n'); })()}>
                      Withdrawals (12m): <span className="font-mono text-red-700">{cash ? fmt(cash.withdrawals) : 'Click history'}</span>
                      {cash?.lastWithdrawal && (
                        <span className="ml-2 text-xs text-gray-600">(last: {new Date(cash.lastWithdrawal.time*1000).toLocaleDateString()})</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1 bg-indigo-600 text-white rounded"
                    onClick={() => togglePositions(item.account.account_number)}
                  >
                    {Object.prototype.hasOwnProperty.call(positionsByAccount || {}, item.account.account_number) ? 'Hide Positions' : 'Load Positions'}
                  </button>
                  {/* History retrieval button (opens modal with charts and journal) */}
                  <MT5HistoryIcon
                    account={{
                      login: item.account.account_number,
                      account_number: item.account.account_number,
                      server: item.account.broker_server || item.account.server || 'Unknown',
                      balance: item.balance ?? 0,
                      equity: item.equity ?? 0
                    }}
                    hideAccountNumber={hideAccountDetails}
                    onCashflow={(cashflow) => {
                      setCashflowByAccount(prev => ({ ...(prev || {}), [accNum]: cashflow }));
                    }}
                  />
                </div>
              </div>
              {Object.prototype.hasOwnProperty.call(positionsByAccount || {}, item.account.account_number) && (
                <div className="mt-4 overflow-x-auto">
                  {positionsLoadingByAccount[item.account.account_number] ? (
                    <div className="p-2 text-sm text-gray-600">Loading positions...</div>
                  ) : null}
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Ticket</th>
                        <th className="text-left p-2">Symbol</th>
                        <th className="text-left p-2">Type</th>
                        <th className="text-left p-2">Volume</th>
                        <th className="text-left p-2">Open Price</th>
                        <th className="text-left p-2">SL</th>
                        <th className="text-left p-2">TP</th>
                        <th className="text-left p-2">Open Time</th>
                        <th className="text-left p-2">Swap</th>
                        <th className="text-left p-2">Profit</th>
                        <th className="text-left p-2">Magic</th>
                        <th className="text-left p-2">Comment</th>
                        <th className="text-left p-2">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!positionsLoadingByAccount[item.account.account_number] && positionsByAccount[item.account.account_number].length === 0 ? (
                        <tr><td className="p-2" colSpan="13">No open positions or pending orders</td></tr>
                      ) : (
                        (positionsByAccount[item.account.account_number] || []).map((p, i) => {
                          const isPending = p.is_pending || p.type?.includes('LIMIT') || p.type?.includes('STOP');
                          return (
                          <tr key={p.ticket || i} className={`border-b hover:bg-gray-50 ${isPending ? 'bg-yellow-50' : ''}`}>
                            <td className="p-2">
                              {p.ticket}
                              {isPending && <span className="ml-1 text-xs bg-yellow-300 px-1 rounded">PENDING</span>}
                            </td>
                            <td className="p-2">{p.symbol}</td>
                            <td className="p-2 font-semibold">{p.type}</td>
                            <td className="p-2">{p.volume}</td>
                            <td className="p-2">{fmtPrice(p.open_price)}</td>
                            <td className="p-2">{fmt(p.sl)}</td>
                            <td className="p-2">{fmt(p.tp)}</td>
                            <td className="p-2">{fmtOpenTime(p)}</td>
                            <td className={`p-2 ${Number(p.swap || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {isPending ? '-' : fmt(p.swap)}
                            </td>
                            <td className={`p-2 ${Number(p.profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {isPending ? '-' : fmt(p.profit)}
                            </td>
                            <td className="p-2 text-xs font-mono text-gray-500">{p.magic ?? '-'}</td>
                            <td className="p-2 text-xs text-gray-600">{p.comment || ''}</td>
                            <td className="p-2">
                              <button
                                onClick={() => handleClosePosition(item.account.account_number, p.ticket)}
                                className="text-red-600 hover:text-red-800 font-bold text-lg"
                                title={isPending ? "Cancel order" : "Close position"}
                              >
                                ×
                              </button>
                            </td>
                          </tr>
                        );})
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );})}
        </div>
      )}
    </div>
  );
};

export default Affiliate;
